/**
 * @author Jonathan Adamssson <jonathan.adamsson@skymap.se>
 *
 * This is a class for representing dynamic asset DXF.
 *
 * Dxf object contains Object3D layers as children, each layer contains
 * it's own entities, dxfLines, texts etc.
 */

import { getTime } from 'date-fns';
import PubSub from 'pubsub-js';
import * as THREE from 'three';
import { Box3, Matrix4, Mesh, Object3D, Vector2, Vector3 } from 'three';
import _ from 'underscore';

import {
  CellGridVolumeModel,
  DynamicAssetType,
  FileVersionModel,
  PrismVolumeModel,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { formattedDate } from '../../utils/dateUtils';
import {
  getVersionNumber,
  sortFileVersionsByRevisionDateAndName,
} from '../../utils/models/file-version';
import { getMaterials } from '../../utils/three/material';
import { isDefined } from '../../utils/variables';
import { AssetVisibilityManager } from '../asset-visibility-manager';
import { SkyviewCamera } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { DxfFactory } from '../loader/dxf/dxf-factory';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import State from '../state';
import { clamp, Point2d } from '../utilities/math-helpers';
import { disposeRecursive } from '../utilities/three/dispose';
// @ts-ignore
import DXFTree from './dxf/DXFTree';
import { DxfUnit, getDxfUnitName, ThreeDxfData } from './dxf/types';
import { AssetLoadingStatus, AssetType, ElementObj } from './element-obj';
import { FileInfo } from './file-info/types';
import { HasVolumes } from './interfaces/has-cell-grid-volumes';
import { HasOpacity } from './interfaces/has-opacity';
import { CellGridVolume } from './volume/cell-grid-volume';
import { PrismVolume } from './volume/prism-volume';
import { VolumeHandler } from './volume/volume-handler';

export type ParseCallbackFunc = (assetId?: string, fileId?: string) => Promise<void>;

export const pstOnDxfDataSet = 'pst_dxfDataSet';

export type DxfOptions = {
  name?: string;
  fileId?: string;
  fileInfo?: FileInfo;
  visible?: boolean;
  timelineContainerId?: string;
  wgs84?: {
    lng: number | string;
    lat: number | string;
  };
  cellGridVolumes?: CellGridVolumeModel[];
  prismVolumes?: PrismVolumeModel[];
  skyMapVolumeSourceIds?: string[];
  height?: number;
  MAS?: number;
  parseEnabled?: boolean;
  assetTagIds?: string[];
};

export class Dxf extends ElementObj implements HasOpacity, HasVolumes {
  get dxfUnit(): DxfUnit {
    return this.unit;
  }

  get dxfUnitName(): string {
    return getDxfUnitName(this.unit);
  }
  get masEditable(): boolean {
    return this._masEditable;
  }
  get masVisible(): boolean {
    return this._masVisible;
  }

  get assetType(): AssetType {
    return 'dxf';
  }
  get parseEnabled(): boolean {
    return this._parseEnabled;
  }
  set parseEnabled(value: boolean) {
    this._parseEnabled = value;
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(opacity: number) {
    this.setOpacityRecursively(opacity);

    this._opacity = opacity;
  }

  origo: Vector3;
  dxfExtents?: Box3;
  fileId?: string;

  isDownloading = false;

  selectedVersion?: FileVersionModel;

  fileInfo?: FileInfo;

  objType: DynamicAssetType = 'Dxf';

  cellGridVolumes: CellGridVolume[] = [];
  prismVolumes: PrismVolume[] = [];
  skyMapVolumeSourceIds: string[] = [];
  volumeHandler = new VolumeHandler(this);

  private _masEditable = false;

  private _masVisible = false;

  private wireframes: Object3D[];

  private sharedViewId?: string;

  private lastUpdate: number;

  private isParsed: boolean;

  private parseCallback: ParseCallbackFunc | undefined;

  private dxfTree: DXFTree | undefined;

  versions: FileVersionModel[] | undefined;
  private path?: string;
  private curVersion: any = undefined;

  uploadedBy: string | undefined;
  uploadDate: string | undefined;
  revision: string | undefined;
  revisionDate: string | undefined;
  dxfScale: string | undefined;
  description: string | undefined;
  private comment: string | undefined;

  private unit: DxfUnit;

  private _parseEnabled = false;
  private _opacity = 1;

  constructor(
    dxfData: ThreeDxfData | undefined,
    options: DxfOptions,
    id: string | undefined,
    sharedViewId?: string,
  ) {
    super(options.timelineContainerId!);

    this.sharedViewId = sharedViewId;

    this.cellGridVolumes = CellGridVolume.fromPropertyArray(options.cellGridVolumes);
    this.prismVolumes = PrismVolume.fromPropertyArray(options.prismVolumes);
    this.skyMapVolumeSourceIds = options.skyMapVolumeSourceIds ?? [];
    this.volumeHandler.activeVolume = this.cellGridVolumes[0] ?? this.prismVolumes[0];

    this._parseEnabled = options.parseEnabled || false;
    options = options || {};
    this.downloadable = true;

    this.fileId = options.fileId;
    this.assetId = id;

    this.wireframes = [];
    this.MAS = options.MAS ?? 0;
    this.origo = new THREE.Vector3(0, 0, 0);

    this.lastUpdate = getTime(new Date());

    this.selectedVersion = undefined;

    this.dxfExtents = undefined;

    this.unit = DxfUnit.Unknown;

    this.wgs84 = this.initPosition();

    this.extractDocumentInformation(options.fileInfo);
    this.setDXFData(dxfData);
    void this.setStateVisibility(options.visible === false ? false : true);

    this.freeze = {
      translate: true,
      rotateZ: true,
      rotateXY: true,
      scale: true,
    };

    this.isParsed = false;

    State.addListener('renderDXFTree', this.toggleTreeRender.bind(this));

    this._assetTagIds = options.assetTagIds ?? [];
  }

  canBeScaled() {
    return false;
  }

  setStateVisibility(state: boolean) {
    this.parseEnabled = true;
    return super.setStateVisibility(state);
  }

  toggleVisibility(visible?: boolean) {
    this.parseEnabled = true;
    return super.toggleVisibility(visible);
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'idle';
  }

  initPosition(): {
    lng: number;
    lat: number;
  } {
    const wgs84Coords = this.calculateWGS84Coords(new Vector2(0, 0));
    return { lng: wgs84Coords?.x ?? 0, lat: wgs84Coords?.y ?? 0 };
  }

  dispose() {
    this.wireframes = [];
    disposeRecursive(this);

    this.remove(...this.children);

    State.removeListener('renderDXFTree', this.toggleTreeRender.bind(this));
  }

  /**
   * Sets the callback function so that it is possible to deplay the parsing.
   * @param callback
   */
  setParseCallback(callback?: ParseCallbackFunc) {
    this.parseCallback = callback;
  }

  /**
   * Starts parsing/loading the DXF if parse is enabled.
   * Calls the parse callback (if any) that executes the parse/load logic.
   */
  async parse() {
    if (!this.visible || !this.parseEnabled || this.isParsed || this.loadingStatus === 'loading') {
      return;
    }

    try {
      this.loadingStatus = 'loading';

      if (this.parseCallback) {
        if (this.fileId) {
          await this.parseCallback(this.selectedVersion?.id, this.fileId);
        } else {
          await this.parseCallback();
        }
      }

      this.isParsed = true;
      this.loadingStatus = 'loaded';

      AssetVisibilityManager.getInstance().publishAssetVisibilityChanged(this);
    } catch (err) {
      this.visible = false;
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
    }
  }

  /**
   * Callback when the state-vislibity is changed.
   * The DXF-asset is to be parsed if it set to visible and not previously parsed.
   */
  async onStateVisibilityChanged() {
    if (this.getStateVisibility()) {
      await this.parse();
      // This adds the volume model to the asset to display it if is visible.
      this.volumeHandler.attachModel();
    } else {
      // This removes the volume model to the asset before clearing the asset mesh data.
      this.volumeHandler.detachModel();
      this.isParsed = false;
      this.clearCustom();
    }

    AssetVisibilityManager.getInstance().publishAssetVisibilityChanged(this);
  }

  setDXFData(dxfData?: ThreeDxfData) {
    if (!dxfData) {
      return;
    }

    this.unit = dxfData.units;

    // this.dxfTree = new DXFTree(this.dxfExtents, { drawTree: false });
    // this.add(this.dxfTree);

    if (!dxfData.containsSurface && !dxfData.contains3dPolyline) {
      // Make MAS editable.
      // Also set visibility to visible (cases when mas is not displayed until asset is visible).
      this._masEditable = true;
      this._masVisible = true;
    }

    let origoOffset = new Vector3(0, 0, 0);

    for (const layer in dxfData.layers) {
      // eslint-disable-next-line no-prototype-builtins
      if (dxfData.layers.hasOwnProperty(layer)) {
        this.add(dxfData.layers[layer].geometry);
        this.getWireframes(dxfData.layers[layer].geometry);
      }
    }

    // Gets the origo from first child node and then transforms all the dxf data nodes based on it.
    const nodes = this.getDxfNodes();
    if (nodes.length > 0) {
      origoOffset = nodes[0].position.clone();
      for (const node of nodes) {
        node.position.sub(origoOffset);
      }
    }
    this.origo = origoOffset;

    this.calculateLocalPosition();
    this.setOpacityRecursively(this._opacity);

    // this.dxfTree.updatePosition(this.position);
    this.loadingStatus = 'loaded';

    /**
     *  Post that the dxf data have been set.
     */
    PubSub.publish(pstOnDxfDataSet, this);
    publish(SubscriptionTopic.AssetChanged, this);
  }

  getDxfNodes(): Object3D[] {
    const nodes: Object3D[] = [];
    this.traverse((child) => {
      if (this.isDxfNode(child) && child.name !== 'Wireframe') {
        nodes.push(child);
      }
    });
    return nodes;
  }

  generateBoundingBox(toWorldCoordinates = false) {
    const bb = super.generateBoundingBox(toWorldCoordinates);

    /**
     * The max suitable size that the bounding box should cover in meters
     */
    const maxBoundingBoxSize = 1000;

    if (bb.min.distanceTo(bb.max) > maxBoundingBoxSize) {
      // iterate through them and if there is any with bb smaller than maxBoundingBoxSize then zoom too that one
      const nodes = this.getDxfNodes();

      nodes.sort((a, b) => {
        // Attempt to count vertices based on buffergeometry
        const countA = (a as Mesh).geometry?.attributes?.position.count ?? 0;
        const countB = (b as Mesh).geometry?.attributes?.position.count ?? 0;

        return countA > countB ? -1 : 1;
      });

      for (const node of nodes) {
        const nodeBoundingBox = new Box3().setFromObject(node);
        if (nodeBoundingBox.min.distanceTo(nodeBoundingBox.max) < maxBoundingBoxSize) {
          return nodeBoundingBox;
        }
      }
    }

    return bb;
  }

  getWireframes(layer: Object3D) {
    if (layer && layer.children) {
      layer.children.map((child) => {
        if (child.name === 'Surface') {
          this.wireframes.push(child.children[0]);
        } else if (child.children.length > 0) {
          this.getWireframes(child);
        }
      });
    }
  }

  extractDocumentInformation(fileInfo?: FileInfo) {
    if (!fileInfo) {
      return;
    }

    if (this.MAS) {
      this._masVisible = true;
    }

    this.fileInfo = fileInfo;
    this.versions = sortFileVersionsByRevisionDateAndName(fileInfo.versions ?? []);
    this.path = fileInfo.path;
    this.curVersion = undefined;

    fileInfo.versions.map(
      (version: FileVersionModel & { labelId?: string; uploadedAt?: string }) => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        version.labelId = version.revision ? `Rev ${version.revision} - ` : '';
        version.labelId += formattedDate(version.uploadedAt ?? version.createdAt);
      },
    );

    this.selectedVersion = this.versions ? this.versions[0] : undefined;
    this.setVersion(this.selectedVersion);
  }

  /**
   * @param version
   * @description Set the version information for this DXF
   */
  setVersion(version?: FileVersionModel) {
    if (!isDefined(version)) {
      return;
    }
    this.name = [this.fileInfo!.name, getVersionNumber(this.versions!, version.id)].join(
      ' - Version #',
    );

    const firstName: string = version.createdBy?.firstName ?? '';
    const lastName: string = version.createdBy?.lastName ?? '';
    this.uploadedBy = `${firstName} ${lastName}`;

    this.uploadDate = version.createdAt;
    this.revision = version.revision;
    this.revisionDate = version.revisionDate;
    this.dxfScale = version.scale;
    this.description = version.description;

    this.curVersion = version;
  }

  async reloadCADData(versionId: string) {
    // This will dispose of current CAD-data
    this.clearCustom();

    try {
      this.loadingStatus = 'loading';
      const parsedDxfResponse = await DxfFactory.instance().parseDXFFile(
        '',
        versionId,
        this.sharedViewId,
        this.fileId,
      );
      if (parsedDxfResponse.error) {
        this.loadingStatus = 'error';
      } else {
        this.setDXFData(parsedDxfResponse.dxf);
        this.loadingStatus = 'loaded';
        this.isParsed = true;
      }
    } catch (err) {
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
    }
  }

  clearCustom() {
    this.dispose();
  }

  async onChange() {
    if (this.selectedVersion && this.curVersion !== this.selectedVersion) {
      this.setVersion(this.selectedVersion);
      // reload data only if it is already parsed
      // No floating-promise error
      if (this.isParsed) {
        await this.reloadCADData(this.selectedVersion.id);
      }
    }

    const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo();

    if (this.MAS !== undefined && projectionOrigo) {
      this.position.z = this.MAS - projectionOrigo.z;
    }

    if (this.autoSave) {
      await this.save();
    }

    publish(SubscriptionTopic.AssetChanged, this);
  }

  getSaveData() {
    const saveData: any = {
      type: this.objType,
      properties: {
        name: this.name,
        visible: this.getStateVisibility(),
        MAS: this.MAS ?? 0,
      },
      fileId: this.fileId ?? undefined,
      timelineContainerId: this.timelineContainerId,
    };

    if (this.assetId) {
      saveData.id = this.assetId;
    }

    const wgs84 = this.calculateWGS84Coords(new Vector2(this.position.x, this.position.y));
    if (wgs84) {
      saveData.properties.wgs84 = {
        lng: wgs84.x.toString(),
        lat: wgs84.y.toString(),
      };
    }

    return Promise.resolve(saveData);
  }

  // To ensure that we use get the id after an asset have been saved
  onSaved(data: any) {
    if (!data) {
      return;
    }

    this.assetId = data.id || this.assetId;
    this.timelineContainerId = data.timelineContainerId;
  }

  getNativeAssetData() {
    return {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
    };
  }

  toggleTreeRender(state: boolean) {
    if (!this.dxfTree) {
      this.dxfTree.setDrawState(state);
    }
  }

  // Executed on each animate
  onAnimate(camera: SkyviewCamera) {
    const newTime = getTime(new Date());
    if (newTime - this.lastUpdate > 1000 && this.dxfTree) {
      const frustum = camera.getFrustum();
      this.dxfTree.frustumCull(camera.getTHREECamera(), frustum);
      this.lastUpdate = newTime;
    }
  }

  onSelect() {
    super.onSelect();

    this.wireframes.map((wireframe) => {
      wireframe.visible = true;
    });
  }

  onDeselect() {
    super.onDeselect();

    this.wireframes.map((wireframe) => {
      wireframe.visible = false;
    });
  }

  /**
   * Gets the matrix from the drawing's coordinates to local coordinates, as the data is shown, for
   * the CAD drawing report. ConvertDwgToGlb keeps the drawing's units in its glb, so the matrix
   * applies to it too. Its nodes were moved by the origo when loaded.
   */
  getSceneToLocalMatrix(): Matrix4 {
    this.updateWorldMatrix(true, false);
    return this.matrixWorld
      .clone()
      .multiply(new Matrix4().makeTranslation(this.origo.clone().negate()));
  }

  calculateLocalPosition(): Point2d | undefined {
    /**
     * Since DXF-files always are georeferenced they don't depend on the WGS 84 coordinates
     * of the asset. We only need to subtract the projection origo.
     */
    const projectionOrigo =
      CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3(0, 0, 0);

    const newPosition = this.origo
      .clone()
      .sub(projectionOrigo)
      .setZ(this.calculateLocalHeight() ?? projectionOrigo.z);

    this.position.set(newPosition.x, newPosition.y, newPosition.z);

    if (this.dxfTree) {
      this.dxfTree.updatePosition(newPosition);
    }

    return;
  }

  equals(dxf: Dxf) {
    const sameId = () => this.assetId === dxf.assetId;
    const sameFile = () => this.fileId === dxf.fileId;
    const sameUpdateTime = () => this.fileInfo?.updatedAt === dxf.fileInfo?.updatedAt;
    const sameVolumeSourceIds = () =>
      _.isEqual(this.skyMapVolumeSourceIds.sort(), dxf.skyMapVolumeSourceIds.sort());
    const sameVersions = () =>
      _.isEqual(this.versions?.map((x) => x.id).sort(), dxf.versions?.map((x) => x.id).sort());

    return sameId() && sameFile() && sameUpdateTime() && sameVolumeSourceIds() && sameVersions();
  }

  private is2DNode(name: string) {
    return ['LineCollection', 'TextCollection', 'PointCollection'].includes(name);
  }

  private setOpacityRecursively(opacity: number) {
    this.traverse((node: Object3D) => {
      /**
       * Give 2D nodes higher opacity than other nodes.
       * This makes it easier to see lines and texts that are hidden behind
       * surfaces for example when adjusting the opacity.
       */
      const nodeOpacity =
        opacity >= 1 ? 1 : this.is2DNode(node.name) ? clamp(opacity * 2, 0, 1) : opacity / 2;

      for (const material of getMaterials(node)) {
        material.opacity = nodeOpacity;
        material.transparent = nodeOpacity < 1;
        material.depthWrite = nodeOpacity === 1;
        material.depthTest = true;
        material.needsUpdate = true;
      }
    });
  }

  private isDxfNode(node: Object3D) {
    return ['Surface', 'Wireframe', 'LineCollection', 'TextCollection', 'PointCollection'].includes(
      node.name,
    );
  }
}
